import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Spin, Typography, App } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import businessUnitService from '../services/businessUnitService';
import PhoneNumberInput from './PhoneNumberInput';

const { Title, Text } = Typography;

const { Option } = Select;

const Register = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [invitationData, setInvitationData] = useState(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState('+27');
  const [hasFormErrors, setHasFormErrors] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    validateInvitation();
  }, [token]);

  // Monitor form errors to disable submit button
  useEffect(() => {
    const fieldsError = form.getFieldsError();
    const hasErrors = fieldsError.some(({ errors }) => errors.length > 0);
    setHasFormErrors(hasErrors);
  }, [form]);

  const validateInvitation = async () => {
    if (!token) {
      setValidating(false);
      // Show a message but don't redirect - allow user to see the form
      message.warning('Registration requires an invitation link. Please contact an administrator.');
      return;
    }

    try {
      setValidating(true);
      const result = await userService.getInvitationDetails(token);
      
      if (result.success) {
        const userData = result.data?.data;
        setInvitationData(userData);
        
        // Pre-fill all available user data
        const formValues = {
          email: userData?.email
        };
        
        // If this is an existing user (has name/surname), pre-fill those fields too
        if (userData?.isExistingUser) {
          formValues.name = userData.name;
          formValues.surname = userData.surname;
          
          // Handle phone number pre-filling
          if (userData.phoneNumber) {
            if (typeof userData.phoneNumber === 'object') {
              formValues.phoneNumber = userData.phoneNumber.number;
              setSelectedCountryCode(userData.phoneNumber.countryCode || '+27');
            } else if (typeof userData.phoneNumber === 'string') {
              // Parse string phone number
              if (userData.phoneNumber.startsWith('+')) {
                // Try to extract country code
                setSelectedCountryCode('+27'); // Default
                formValues.phoneNumber = userData.phoneNumber.replace(/^\+27/, '');
              } else {
                formValues.phoneNumber = userData.phoneNumber;
              }
            }
          }
        }
        
        form.setFieldsValue(formValues);
        message.success('Invitation validated successfully!');
      } else {
        const isExpired = result.message && result.message.includes('expired');
        if (isExpired) {
          message.error('This invitation link has expired. Please contact an administrator for a new invitation.');
        } else {
          message.error(result.message || 'Invalid invitation link');
        }
        // Don't redirect for invalid tokens - let user see the error and contact admin
        setValidating(false);
      }
    } catch (error) {
      console.error('Error validating invitation:', error);
      message.error('Failed to validate invitation - please contact administrator');
      // Don't redirect on error - let user see the form
      setValidating(false);
    } finally {
      setValidating(false);
    }
  };

  const onFinish = async (values) => {
    if (!token) {
      message.error('Registration requires a valid invitation token. Please contact an administrator to receive an invitation.');
      return;
    }

    setLoading(true);
    try {
      const result = await userService.completeRegistration({
        token,
        name: values.name,
        surname: values.surname,
        phoneNumber: {
          countryCode: selectedCountryCode,
          number: values.phoneNumber,
          full: `${selectedCountryCode}${values.phoneNumber}`
        },
        password: values.password
      });

      if (result.success) {
        message.success('Registration completed successfully!');
        // Redirect to login with success message
        navigate('/login?registered=true');
      } else {
        // Show generic error message for any registration failure
        if (result.message && (result.message.includes('email') || result.message.includes('phone') || result.message.includes('duplicate') || result.message.includes('exists'))) {
          message.error('Email or phone number already used. Please contact your administrator.');
        } else {
          message.error(result.message || 'Registration failed. Please contact your administrator.');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      message.error('Registration failed. Please contact your administrator.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <Card style={{ padding: '40px', textAlign: 'center' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#666' }}>Validating invitation...</p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: window.innerWidth <= 768 ? '10px' : '20px'
    }}>
      <Card style={{ 
        width: '100%', 
        maxWidth: 500, 
        padding: window.innerWidth <= 768 ? '15px' : '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        borderRadius: '8px',
        margin: window.innerWidth <= 768 ? '0' : 'auto'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <img 
            src="/logo.png" 
            alt="TapLearnGo Logo"
            className="w-3/4 h-auto mx-auto"
            style={{ backgroundColor: 'transparent', marginTop: '-20px' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <Text type="secondary">Creating a culture of learning</Text>
        </div>

        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>Register</Title>
          
          {token && !invitationData && !validating && (
            <div style={{ 
              marginTop: '10px', 
              padding: '12px', 
              background: '#fff2f0', 
              borderRadius: '6px',
              fontSize: '14px',
              color: '#cf1322',
              border: '1px solid #ffccc7'
            }}>
              <Text strong>Invitation Link Issue</Text>
              <div style={{ marginTop: '4px' }}>
                This invitation link appears to be invalid or expired. Please contact an administrator for assistance.
              </div>
            </div>
          )}

          {!token && (
            <div style={{ 
              marginTop: '10px', 
              padding: '12px', 
              background: '#fff7e6', 
              borderRadius: '6px',
              fontSize: '14px',
              color: '#d46b08',
              border: '1px solid #ffd591'
            }}>
              <Text strong>Invitation Required</Text>
              <div style={{ marginTop: '4px' }}>
                Registration requires an invitation link. Please contact an administrator to receive one.
              </div>
            </div>
          )}
          
          {invitationData && (
            <div style={{ 
              marginTop: '10px', 
              padding: '10px', 
              background: '#f0f8ff', 
              borderRadius: '4px',
              fontSize: '14px',
              color: '#666'
            }}>
              <div><strong>Group:</strong> {invitationData.group?.groupName}</div>
              <div><strong>Email:</strong> {invitationData.email}</div>
              {invitationData.isExistingUser && (
                <div style={{ marginTop: '8px', color: '#1890ff', fontSize: '13px' }}>
                  ℹ️ Your name, surname, email, and phone number are pre-filled and cannot be changed.
                  Only set your password to complete registration.
                </div>
              )}
            </div>
          )}
        </div>

        <Form
          form={form}
          name="register"
          onFinish={onFinish}
          layout="vertical"
          size="medium"
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input 
              prefix={<MailOutlined />} 
              placeholder="Email" 
              disabled={!!invitationData} // Only disable if we have valid invitation data
              style={invitationData ? { background: '#f5f5f5' } : {}}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Please enter your name!' },
              { min: 2, message: 'Name must be at least 2 characters' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Name" 
              disabled={invitationData?.isExistingUser}
              style={invitationData?.isExistingUser ? { background: '#f5f5f5' } : {}}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="surname"
            label="Surname"
            rules={[
              { required: true, message: 'Please enter your surname!' },
              { min: 2, message: 'Surname must be at least 2 characters' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Surname" 
              disabled={invitationData?.isExistingUser}
              style={invitationData?.isExistingUser ? { background: '#f5f5f5' } : {}}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password (6 digits at least, case sensitive)"
            rules={[
              { required: true, message: 'Please enter your password!' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Password" 
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Confirm password" 
              size="large"
            />
          </Form.Item>

          <PhoneNumberInput
            form={form}
            businessUnitId={invitationData?.businessUnitId}
            initialPhone=""
            initialCountryCode={selectedCountryCode}
            onCountryCodeChange={setSelectedCountryCode}
            disabled={invitationData?.isExistingUser}
            onPhoneValidation={(isValid, phone, countryCode) => {
              if (isValid) {
                form.setFieldsValue({ phoneNumber: phone });
              }
            }}
          />

          <Form.Item
            name="verificationCode"
            label="Verification code"
            rules={[
              { required: true, message: 'Please enter the verification code!' },
              { len: 6, message: 'Verification code must be 6 characters!' }
            ]}
            style={{ marginTop: '16px' }}
          >
            <Input.Group compact>
              <Input 
                style={{ width: '70%' }} 
                placeholder="Enter verification code from email"
                maxLength={6}
                
                onChange={(e) => {
                  // Auto-uppercase the verification code
                  const value = e.target.value.toUpperCase();
                  form.setFieldsValue({ verificationCode: value });
                }}
              />
              <Button  style={{ width: '30%' }} type="default">
                Get Code
              </Button>
            </Input.Group>
          </Form.Item>

          <Form.Item style={{ marginTop: '24px', marginBottom: '16px' }}>
            <Button 
              htmlType="submit" 
              loading={loading}
              disabled={hasFormErrors}
              style={{ 
                width: '100%', 
                height: '40px',
                background: '#2E86AB',
                borderColor: '#2E86AB',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Register
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Button 
              type="link" 
              onClick={() => navigate('/login')}
              style={{ color: '#2E86AB' }}
            >
              Back to Login
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Register;